import { AddOrEditIntegrationDrawerFooter } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerFooter';
import { AddOrEditIntegrationDrawerHeader } from '@modules/integrationsV2/components/AddOrEditIntegrationDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import {
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { EditIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { Box, Stack } from '@mui/material';
import { DescriptionMap } from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

export const EditIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationChannel,
  integrationType,
  channelID,
  channelName,
  channelDescriptions,
}: EditIntegrationChannelDrawerContentProps) => {
  const currentDescription =
    channelDescriptions.find(
      (channelDescription) => channelDescription.sourceID === channelID,
    )?.description ?? '';

  const [descriptionInput, setDescriptionInput] = useState(currentDescription);

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: integrationChannel.type,
      integrationType,
      onCompleted: () => {
        onDrawerClose();
      },
    });

  const [
    submitForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: integrationChannel.type,
    integrationType,
    hasValidFormFields: !!descriptionInput.trim(),
    descriptionInput,
    currentDescription,
    onValidated: async () => {
      let updatedDescriptionMap: DescriptionMap[];

      const isChannelDescriptionPresent = channelDescriptions.some(
        (channelDescription) => channelDescription.sourceID === channelID,
      );

      if (isChannelDescriptionPresent) {
        updatedDescriptionMap = channelDescriptions.map(
          (channelDescription) => {
            if (channelDescription.sourceID === channelID) {
              return {
                ...channelDescription,
                description: descriptionInput.trim(),
              };
            }

            return channelDescription;
          },
        );
      } else {
        updatedDescriptionMap = [
          ...channelDescriptions,
          {
            sourceID: channelID,
            description: descriptionInput.trim(),
          },
        ];
      }

      updateIntegration({
        integrationID,
        isActive: true,
        updateDescription: {
          descriptionMap: updatedDescriptionMap,
        },
      });
    },
  });

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddOrEditIntegrationDrawerHeader
          integrationChannel={integrationChannel}
          integrationType={integrationType}
          title={`Edit ${integrationChannel.displayName} Integration`}
          subtitle={channelName}
        />
        <Box p={3}>
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            isValidationInProgress={isValidatingDescription}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Box>
      </Stack>
      <AddOrEditIntegrationDrawerFooter
        onCancel={onDrawerClose}
        onSubmit={submitForm}
        isDisabled={isDisabled}
        isLoading={IsLoading(updateIntegrationStatus)}
      />
    </Stack>
  );
};
